/**
 * 颜色派生工具 —— 等价于 CSS `color-mix(in srgb, A p%, B)`。
 *
 * 背景：三个落地页的卡片主色（--accent/--brand/--step/--icon）由 JS 运行时注入，
 * less 里原先用 color-mix() 基于主色派生边框/浅底/压暗文字等颜色。但 color-mix()
 * 在旧 iPad Safari(<16.2) 不支持，需靠兜底降级。改为在 JS 侧预先算好派生色、
 * 注入成额外 CSS 变量后，less 用 var() 即可，旧机（支持 var 即可）也能吃到精确派生色。
 *
 * 计算严格对齐 CSS 规范：in srgb 表示在 sRGB 编码值上加权平均，含透明度时按
 * premultiplied alpha 插值。因此 `mix(C, 'transparent', p)` === `rgba(C, p/100)`。
 */

// 解析 #rgb / #rrggbb / rgb() / rgba() / transparent → {r,g,b,a}
function parseColor(input) {
  var s = String(input).trim().toLowerCase();
  if (s === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };

  if (s.charAt(0) === '#') {
    var hex = s.slice(1);
    if (hex.length === 3) hex = hex.charAt(0) + hex.charAt(0) + hex.charAt(1) + hex.charAt(1) + hex.charAt(2) + hex.charAt(2);
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: 1,
    };
  }

  var m = s.match(/rgba?\(([^)]+)\)/);
  if (m) {
    var parts = m[1].split(',').map(function (x) { return x.trim(); });
    return {
      r: parseFloat(parts[0]),
      g: parseFloat(parts[1]),
      b: parseFloat(parts[2]),
      a: parts[3] === undefined ? 1 : parseFloat(parts[3]),
    };
  }

  throw new Error('colorMix: 无法解析颜色 "' + input + '"');
}

function clampByte(v) {
  v = Math.round(v);
  if (v < 0) return 0;
  if (v > 255) return 255;
  return v;
}

// 输出：alpha≈1 → #rrggbb；否则 → rgba(r,g,b,a)
function formatColor(c) {
  var r = clampByte(c.r);
  var g = clampByte(c.g);
  var b = clampByte(c.b);
  if (c.a >= 0.999) {
    var hex = function (n) { return (n < 16 ? '0' : '') + n.toString(16); };
    return '#' + hex(r) + hex(g) + hex(b);
  }
  var a = Math.round(c.a * 1000) / 1000;
  return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
}

/**
 * a 占 percent%，b 占 (100-percent)%，等价 color-mix(in srgb, a percent%, b)。
 * @param {string} a 第一颜色（通常为主色）
 * @param {string} b 第二颜色（transparent / #ffffff / 深色等）
 * @param {number} percent a 的占比（0-100）
 * @returns {string}
 */
export function mix(a, b, percent) {
  var ca = parseColor(a);
  var cb = parseColor(b);
  // 浏览器按 8-bit 存储 sRGB 颜色，含小数 alpha（如 .12）会先量化到 n/255 再参与混合。
  // 这里对齐该行为，保证派生值与浏览器原生 color-mix 渲染逐通道零误差。
  ca.a = Math.round(ca.a * 255) / 255;
  cb.a = Math.round(cb.a * 255) / 255;
  var pa = percent / 100;
  var pb = 1 - pa;

  var alpha = ca.a * pa + cb.a * pb;

  var r, g, bch;
  if (alpha === 0) {
    r = 0; g = 0; bch = 0;
  } else {
    // premultiplied sRGB 插值后再 un-premultiply
    r = (ca.r * ca.a * pa + cb.r * cb.a * pb) / alpha;
    g = (ca.g * ca.a * pa + cb.g * cb.a * pb) / alpha;
    bch = (ca.b * ca.a * pa + cb.b * cb.a * pb) / alpha;
  }

  return formatColor({ r: r, g: g, b: bch, a: alpha });
}

export default mix;
